import { supabase } from '@/integrations/supabase/client';

export type FeaturedItemType = 'mentoria' | 'mentor' | 'comunidade';

export interface FeaturedItem {
  id: string;
  item_type: FeaturedItemType;
  item_id: string;
  position: number;
  featured_on_home: boolean;
  featured_on_page: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface CreateFeaturedItemData {
  item_type: FeaturedItemType;
  item_id: string;
  position: number;
  featured_on_home?: boolean;
  featured_on_page?: boolean;
}

export interface UpdateFeaturedItemData {
  position?: number;
  featured_on_home?: boolean;
  featured_on_page?: boolean;
}

class FeaturedItemsService {
  // Listar todos os itens em destaque
  async getFeaturedItems(itemType?: FeaturedItemType): Promise<FeaturedItem[]> {
    try {
      let query = supabase
        .from('featured_items')
        .select('*')
        .order('position', { ascending: true });

      if (itemType) {
        query = query.eq('item_type', itemType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar itens em destaque:', error);
        throw error;
      }

     return (data || []) as FeaturedItem[];
    } catch (error) {
      console.error('Erro no serviço getFeaturedItems:', error);
      throw error;
    }
  }

  // Buscar itens em destaque para a home
  async getFeaturedItemsForHome(itemType: FeaturedItemType): Promise<FeaturedItem[]> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', itemType)
        .eq('featured_on_home', true)
        .order('position', { ascending: true });

      if (error) {
        console.error('Erro ao buscar itens em destaque para home:', error);
        throw error;
      }

     return (data || []) as FeaturedItem[];
    } catch (error) {
      console.error('Erro no serviço getFeaturedItemsForHome:', error);
      throw error;
    }
  }

  // Buscar itens em destaque para página específica
  async getFeaturedItemsForPage(itemType: FeaturedItemType): Promise<FeaturedItem[]> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', itemType)
        .eq('featured_on_page', true)
        .order('position', { ascending: true });

      if (error) {
        console.error('Erro ao buscar itens em destaque para página:', error);
        throw error;
      }

      return (data || []) as FeaturedItem[];
    } catch (error) {
      console.error('Erro no serviço getFeaturedItemsForPage:', error);
      throw error;
    }
  }

  // Verificar se um item está em destaque
  async isItemFeatured(itemType: FeaturedItemType, itemId: string): Promise<FeaturedItem | null> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', itemType)
        .eq('item_id', itemId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Erro ao verificar se item está em destaque:', error);
        throw error;
      }

      return (data || null) as FeaturedItem | null;
    } catch (error) {
      console.error('Erro no serviço isItemFeatured:', error);
      throw error;
    }
  }

  // Criar novo item em destaque
  async createFeaturedItem(data: CreateFeaturedItemData): Promise<FeaturedItem> {
    try {
      // Verificar se a posição já está ocupada
      const existingPosition = await this.getFeaturedItemByPosition(data.item_type, data.position);
      if (existingPosition) {
        throw new Error(`Posição ${data.position} já está ocupada para ${data.item_type}`);
      }

      // Verificar se o item já está em destaque
      const existingItem = await this.isItemFeatured(data.item_type, data.item_id);
      if (existingItem) {
        throw new Error(`Este ${data.item_type} já está em destaque na posição ${existingItem.position}`);
      }

      const { data: newItem, error } = await supabase
        .from('featured_items')
        .insert({
          ...data,
          featured_on_home: data.featured_on_home ?? true,
          featured_on_page: data.featured_on_page ?? true,
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar item em destaque:', error);
        throw error;
      }

      return newItem as FeaturedItem;
    } catch (error) {
      console.error('Erro no serviço createFeaturedItem:', error);
      throw error;
    }
  }

  // Atualizar item em destaque
  async updateFeaturedItem(id: string, data: UpdateFeaturedItemData): Promise<FeaturedItem> {
    try {
      // Se está mudando a posição, verificar se a nova posição está disponível
      if (data.position) {
        const currentItem = await this.getFeaturedItemById(id);
        if (!currentItem) {
          throw new Error('Item em destaque não encontrado');
        }

        if (data.position !== currentItem.position) {
          const existingPosition = await this.getFeaturedItemByPosition(currentItem.item_type, data.position);
          if (existingPosition && existingPosition.id !== id) {
            throw new Error(`Posição ${data.position} já está ocupada para ${currentItem.item_type}`);
          }
        }
      }

      const { data: updatedItem, error } = await supabase
        .from('featured_items')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar item em destaque:', error);
        throw error;
      }

      return updatedItem as FeaturedItem;
    } catch (error) {
      console.error('Erro no serviço updateFeaturedItem:', error);
      throw error;
    }
  }

  // Remover item em destaque
  async removeFeaturedItem(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('featured_items')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao remover item em destaque:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro no serviço removeFeaturedItem:', error);
      throw error;
    }
  }

  // Buscar item em destaque por ID
  async getFeaturedItemById(id: string): Promise<FeaturedItem | null> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar item em destaque por ID:', error);
        throw error;
      }

      return (data || null) as FeaturedItem | null;
    } catch (error) {
      console.error('Erro no serviço getFeaturedItemById:', error);
      throw error;
    }
  }

  // Buscar item em destaque por posição
  async getFeaturedItemByPosition(itemType: FeaturedItemType, position: number): Promise<FeaturedItem | null> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', itemType)
        .eq('position', position)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar item em destaque por posição:', error);
        throw error;
      }

      return (data || null) as FeaturedItem | null;
    } catch (error) {
      console.error('Erro no serviço getFeaturedItemByPosition:', error);
      throw error;
    }
  }

  // Trocar posições de dois itens
  async swapPositions(id1: string, id2: string): Promise<void> {
    try {
      console.log(`🔄 Iniciando swap entre ${id1} e ${id2}`);
      
      const [item1, item2] = await Promise.all([
        this.getFeaturedItemById(id1),
        this.getFeaturedItemById(id2)
      ]);

      if (!item1 || !item2) {
        throw new Error('Um ou ambos os itens não foram encontrados');
      }

      if (item1.item_type !== item2.item_type) {
        throw new Error('Não é possível trocar posições entre tipos diferentes');
      }

      console.log(`📍 Item1: posição ${item1.position}, Item2: posição ${item2.position}`);

      // Usar posição temporária única baseada em timestamp para evitar conflitos
      const tempPosition = 1000 + Date.now() % 1000;
      
      // Passo 1: Mover item1 para posição temporária
      console.log(`🔄 Passo 1: Movendo item1 para posição temporária ${tempPosition}`);
      const { error: error1 } = await supabase
        .from('featured_items')
        .update({ position: tempPosition })
        .eq('id', id1);

      if (error1) {
        console.error('❌ Erro no passo 1:', error1);
        throw error1;
      }

      // Passo 2: Mover item2 para posição do item1
      console.log(`🔄 Passo 2: Movendo item2 para posição ${item1.position}`);
      const { error: error2 } = await supabase
        .from('featured_items')
        .update({ position: item1.position })
        .eq('id', id2);

      if (error2) {
        console.error('❌ Erro no passo 2:', error2);
        // Reverter passo 1
        await supabase
          .from('featured_items')
          .update({ position: item1.position })
          .eq('id', id1);
        throw error2;
      }

      // Passo 3: Mover item1 da posição temporária para posição do item2
      console.log(`🔄 Passo 3: Movendo item1 para posição ${item2.position}`);
      const { error: error3 } = await supabase
        .from('featured_items')
        .update({ position: item2.position })
        .eq('id', id1);

      if (error3) {
        console.error('❌ Erro no passo 3:', error3);
        // Reverter passos anteriores
        await supabase
          .from('featured_items')
          .update({ position: item2.position })
          .eq('id', id2);
        await supabase
          .from('featured_items')
          .update({ position: item1.position })
          .eq('id', id1);
        throw error3;
      }

      console.log(`✅ Posições trocadas com sucesso: ${item1.position} ↔ ${item2.position}`);
    } catch (error) {
      console.error('❌ Erro no serviço swapPositions:', error);
      throw error;
    }
  }

  // Obter estatísticas dos destaques
  async getFeaturedStats(): Promise<{
    mentoria: number;
    mentor: number;
    comunidade: number;
    total: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('featured_items')
        .select('item_type');

      if (error) {
        console.error('Erro ao buscar estatísticas dos destaques:', error);
        throw error;
      }

      const stats = {
        mentoria: 0,
        mentor: 0,
        comunidade: 0,
        total: data?.length || 0
      };

      data?.forEach(item => {
        if (item.item_type in stats) {
          stats[item.item_type as keyof typeof stats]++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Erro no serviço getFeaturedStats:', error);
      throw error;
    }
  }
}

export const featuredItemsService = new FeaturedItemsService(); 